/**
 * profile-store
 * -------------
 * Small localStorage-backed helper for persisting the user's display name
 * (and, in future, avatar color) across sessions. All functions are safe to
 * call during SSR — they no-op / return null when `window` is undefined.
 */

const STORAGE_KEY = "syncscreen:profile";

export interface Profile {
  name: string;
}

/**
 * Read the saved profile from localStorage. Returns null on the server, when
 * storage is empty, or when the stored value is malformed.
 */
export function getProfile(): Profile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as Profile).name === "string" &&
      (parsed as Profile).name.trim().length > 0
    ) {
      return { name: (parsed as Profile).name };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist a display name to localStorage. Empty / whitespace-only names are
 * ignored. No-ops on the server.
 */
export function saveProfile(name: string): void {
  if (typeof window === "undefined") return;
  try {
    const cleaned = (name ?? "").trim();
    if (!cleaned) return;
    const profile: Profile = { name: cleaned };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch {
    // Ignore quota / privacy-mode errors.
  }
}

/**
 * Remove the saved profile from localStorage. No-ops on the server.
 */
export function clearProfile(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
